import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { TID } from "@/constants/testIds";
import DotHalo from "@/components/DotHalo";

const FIELD =
  "h-[54px] w-full rounded-[14px] border bg-white px-4 text-[17px] text-[#1D1D1F] outline-none transition-all placeholder:text-[#B0B0B8] focus:border-[#0A66FF] focus:ring-[3px] focus:ring-[#0A66FF]/20";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const ready = password.length >= 6 && confirm.length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-center bg-white px-6 py-14 sm:items-center sm:py-16">
      <div className="w-full max-w-[460px]">
        <div className="flex justify-center">
          <DotHalo>
            <img
              src="/meranggi-logo.png"
              alt="Meranggi"
              className="h-[96px] w-[96px] rounded-full object-cover"
            />
          </DotHalo>
        </div>

        <h1 className="fade-up mt-4 text-center text-[22px] font-bold leading-[1.15] tracking-[-0.02em] text-[#1D1D1F] sm:text-[28px]">
          Reset your password
        </h1>

        {!token ? (
          <p
            data-testid={TID.reset.error}
            className="fade-up mt-8 rounded-[14px] bg-[#FDF0F2] px-4 py-3 text-center text-[14.5px] text-[#C8102E]"
          >
            Invalid or missing reset link.
          </p>
        ) : done ? (
          <div
            data-testid={TID.reset.done}
            className="fade-up mt-8 flex flex-col items-center gap-3 text-center"
          >
            <CheckCircle2 className="h-12 w-12 text-[#34C759]" />
            <p className="text-[16px] text-[#1D1D1F]">
              Password updated. Redirecting to sign in…
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="fade-up mt-10 space-y-3" style={{ animationDelay: "80ms" }}>
            <input
              data-testid={TID.reset.password}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              autoFocus
              placeholder="New password (min. 6 characters)"
              className={`${FIELD} border-[#D2D2D7]`}
            />
            <input
              data-testid={TID.reset.confirm}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Confirm new password"
              className={`${FIELD} border-[#D2D2D7]`}
            />

            {error && (
              <p
                data-testid={TID.reset.error}
                className="fade-up rounded-[14px] bg-[#FDF0F2] px-4 py-3 text-[14.5px] text-[#C8102E]"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid={TID.reset.submit}
              disabled={busy || !ready}
              className={`mt-3 flex h-[54px] w-full items-center justify-center gap-2 rounded-[14px] text-[17px] font-medium text-white transition-all duration-200 ${
                ready
                  ? "bg-[#0071E3] hover:bg-[#0077ED] active:scale-[0.99]"
                  : "cursor-not-allowed bg-[#0071E3]/30"
              }`}
            >
              {busy && <Loader2 className="h-[18px] w-[18px] animate-spin" />}
              Update password
            </button>

            <button
              type="button"
              data-testid={TID.reset.backLink}
              onClick={() => navigate("/login")}
              className="mt-2 w-full text-center text-[15px] font-medium text-[#0071E3] hover:underline"
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
